import { useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { ActivityDto, WeeklyDto } from '../../shared/api';
import { ACTIONS, MAX_USERS, ROLE_LABEL, type Role } from '../../shared/domain';
import { get, patch, post, refresh } from '../api';
import { ActTable, Bar, Err, ItemLink, Spinner, Tag, errMsg, hm, pl, useApp, ymd } from '../components/ui';

export function DailyReport() {
  const { d, toast, openArticle } = useApp();
  const boxes = d.b.items.filter((i) => d.artOf(i).consumable && i.status === 'in');
  const [no, setNo] = useState(boxes[0]?.no ?? '');
  const [left, setLeft] = useState('');
  const [why, setWhy] = useState<'Used' | 'Broken'>('Used');
  const [proj, setProj] = useState('');
  const [err, setErr] = useState('');
  const todays = useQuery({ queryKey: ['acts', 'daily'], queryFn: () => get<ActivityDto[]>('/api/activities?action=Daily%20report&limit=50') });
  const td = (todays.data ?? []).filter((a) => ymd(a.at) === d.today);
  const save = async () => {
    setErr('');
    try {
      const r = await post<{ text: string }>('/api/daily-report', { no, left: parseFloat(left), why, project: proj });
      await refresh();
      setLeft('');
      toast(r.text);
    } catch (e) {
      setErr(errMsg(e));
    }
  };
  return (
    <>
      <h1>Daily report</h1>
      <p className="sub">At the end of each day, write how much is left in each consumables box you used: screws, bolts, blades, drill bits. The app works out what was used, and warns the warehouse owner when an article falls below its minimum.</p>
      <div className="panel row" style={{ alignItems: 'flex-end' }}>
        <label className="field" style={{ minWidth: 240 }}>
          Box
          <select value={no} onChange={(e) => setNo(e.target.value)}>
            {boxes.map((i) => (
              <option key={i.no} value={i.no}>
                {i.no} {d.artOf(i).name} ({d.fmtQty(i)})
              </option>
            ))}
          </select>
        </label>
        <label className="field" style={{ width: 130 }}>
          How much is left now
          <input type="number" min={0} step="any" value={left} onChange={(e) => setLeft(e.target.value)} />
        </label>
        <label className="field">
          Used or broken
          <select value={why} onChange={(e) => setWhy(e.target.value as 'Used' | 'Broken')}>
            <option>Used</option>
            <option>Broken</option>
          </select>
        </label>
        <label className="field" style={{ minWidth: 200 }}>
          Project (optional)
          <select value={proj} onChange={(e) => setProj(e.target.value)}>
            <option value="">No project</option>
            {d.b.projects.map((p) => (
              <option key={p.code} value={p.code}>
                {p.code} {p.name}
              </option>
            ))}
          </select>
        </label>
        <button className="btn primary" disabled={!no || left === ''} onClick={() => void save()}>
          Save
        </button>
      </div>
      <Err e={err} />
      <h2>Consumables</h2>
      <div className="grid">
        {d.b.articles
          .filter((a) => a.consumable)
          .map((a) => {
            const st = d.artStock(a);
            return (
              <button key={a.id} className="panel" style={{ textAlign: 'left' }} onClick={() => openArticle(a.id)}>
                <b>{a.name}</b>
                <div className="meta">
                  {+st.toFixed(2)} {a.unit} in total, minimum {a.min || 'not set'}
                </div>
                <Bar q={st} min={a.min} />
                {d.artLow(a) && (
                  <span className="tag bad" style={{ marginTop: 6 }}>
                    Reorder
                  </span>
                )}
              </button>
            );
          })}
      </div>
      <h2>Reported today ({td.length})</h2>
      <ActTable list={td} compact />
    </>
  );
}

export function Activity({ initialUser = '' }: { initialUser?: string }) {
  const { d } = useApp();
  const [user, setUser] = useState(initialUser);
  const [action, setAction] = useState('');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const params = (before?: number) => {
    const p = new URLSearchParams({ limit: '100' });
    if (user) p.set('user', user);
    if (action) p.set('action', action);
    if (search) p.set('q', search);
    if (before) p.set('before', String(before));
    return p.toString();
  };
  const r = useInfiniteQuery({
    queryKey: ['acts', 'log', user, action, search],
    queryFn: ({ pageParam }) => get<ActivityDto[]>(`/api/activities?${params(pageParam || undefined)}`),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.length === 100 ? last[last.length - 1]!.id : undefined),
  });
  const list = r.data?.pages.flat() ?? [];
  return (
    <>
      <h1>Activity log</h1>
      <p className="sub">Every action is saved here with the user, date and time. The item number is the ID of each activity. Nobody can change or delete an entry.</p>
      <div className="row" style={{ marginBottom: 12 }}>
        <select value={user} onChange={(e) => setUser(e.target.value)}>
          <option value="">All users</option>
          {d.b.users.map((u) => (
            <option key={u.id}>{u.name}</option>
          ))}
        </select>
        <select value={action} onChange={(e) => setAction(e.target.value)}>
          <option value="">All actions</option>
          {ACTIONS.map((a) => (
            <option key={a}>{a}</option>
          ))}
        </select>
        <input placeholder="Item number or word" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && setSearch(q)} onBlur={() => setSearch(q)} />
        <span className="meta">{list.length} entries</span>
      </div>
      {r.isLoading ? <Spinner /> : <ActTable list={list} />}
      {r.hasNextPage && (
        <button className="btn more" disabled={r.isFetchingNextPage} onClick={() => void r.fetchNextPage()}>
          Show older entries
        </button>
      )}
    </>
  );
}

export function Weekly() {
  const [week, setWeek] = useState(0);
  const r = useQuery({ queryKey: ['weekly', week], queryFn: () => get<WeeklyDto>(`/api/weekly?week=${week}`) });
  const saved = useQuery({ queryKey: ['weekly-saved'], queryFn: () => get<{ weekStart: string; emailedAt: string | null; emailError: string | null }[]>('/api/weekly/saved') });
  const w = r.data;
  return (
    <>
      <h1>Weekly report</h1>
      <p className="sub">Which items were taken out and which items were added, Monday to Sunday. Every Friday afternoon the app saves this report and emails it to the warehouse owner.</p>
      <div className="row" style={{ marginBottom: 12 }}>
        {[
          [0, 'This week'],
          [1, 'Last week'],
          [2, '2 weeks ago'],
          [3, '3 weeks ago'],
        ].map(([k, l]) => (
          <button key={k} className={`btn small ${week === k ? 'on' : ''}`} onClick={() => setWeek(k as number)}>
            {l}
          </button>
        ))}
      </div>
      {!w ? (
        <Spinner />
      ) : (
        <>
          <div className="panel">
            <h3 style={{ margin: '0 0 4px' }}>
              Week of {w.weekStart} to {w.weekEnd}
            </h3>
            <p style={{ margin: 0 }}>
              <b>{pl(w.takenOut.length, 'item')} taken out</b> and <b>{pl(w.added.length, 'item')} added</b>. Also {pl(w.moves, 'move')}, {pl(w.qcChecks, 'QC check')} and {pl(w.dailyReports, 'consumables report')}.
            </p>
            {(() => {
              const s = saved.data?.find((x) => x.weekStart === w.weekStart);
              if (!s) return null;
              return <p className="meta" style={{ margin: '6px 0 0' }}>{s.emailedAt ? `Emailed on ${ymd(s.emailedAt)}, ${hm(s.emailedAt)}.` : s.emailError ? 'Saved. The email could not be sent.' : 'Saved in the app.'}</p>;
            })()}
          </div>
          <h2>Taken out ({w.takenOut.length})</h2>
          {w.takenOut.length ? (
            <WeekTable rows={w.takenOut} col="from" />
          ) : (
            <p className="meta">Nothing was taken out this week.</p>
          )}
          <h2>Added ({w.added.length})</h2>
          {w.added.length ? <WeekTable rows={w.added} col="to" /> : <p className="meta">Nothing was added this week.</p>}
        </>
      )}
    </>
  );
}

function WeekTable({ rows, col }: { rows: WeeklyDto['takenOut']; col: 'from' | 'to' }) {
  return (
    <div className="panel scroll" style={{ padding: '4px 8px' }}>
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Time</th>
            <th>User</th>
            <th>Item number</th>
            <th>Article</th>
            <th>{col === 'from' ? 'From' : 'Put on'}</th>
            <th>{col === 'from' ? 'Description' : 'New or returned'}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.id}>
              <td style={{ whiteSpace: 'nowrap' }}>{ymd(a.at)}</td>
              <td>{hm(a.at)}</td>
              <td>{a.user}</td>
              <td>
                <ItemLink no={a.item} />
              </td>
              <td>{a.article}</td>
              <td className="code">{a[col]}</td>
              <td>{col === 'from' ? a.desc : a.from === 'New' ? <Tag cls="ok">New item</Tag> : a.from === 'Sheet' ? <Tag cls="soft">Imported</Tag> : <Tag cls="soft">Returned</Tag>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Users({ onOpenUser }: { onOpenUser: (name: string) => void }) {
  const { d, toast } = useApp();
  const [period, setPeriod] = useState<'7' | '30' | 'all'>('30');
  const rep = useQuery({ queryKey: ['userReport', period], queryFn: () => get<{ counts: { user: string; action: string; n: number }[]; last: { user: string; at: string }[] }>(`/api/users/report?period=${period}`) });
  const [nu, setNu] = useState({ name: '', role: 'user' as Role, pin: '' });
  const [pinFor, setPinFor] = useState<{ id: number; pin: string } | null>(null);
  const [mine, setMine] = useState({ old: '', pin: '' });
  const [err, setErr] = useState('');
  const kinds = ['Register', 'Take out', 'Move', 'QC check', 'Suitcase check', 'Daily report', 'Note'];
  const active = d.b.users.filter((u) => u.active).length;
  const run = async (fn: () => Promise<unknown>, msg: string) => {
    setErr('');
    try {
      await fn();
      await refresh();
      toast(msg);
      return true;
    } catch (e) {
      setErr(errMsg(e));
      return false;
    }
  };
  const roleOf = (id: number, role: Role) => ROLE_LABEL[role] + (d.b.checkerId === id ? ', Checker on duty' : '');
  const count = (u: string, k: string) => rep.data?.counts.find((c) => c.user === u && c.action === k)?.n ?? 0;
  const total = (u: string) => rep.data?.counts.filter((c) => c.user === u).reduce((s, c) => s + c.n, 0) ?? 0;
  return (
    <>
      <h1>Users</h1>
      <p className="sub">The app has a fixed list of users (up to {MAX_USERS}). Only the warehouse owner can add or remove users. Removed users keep their history.</p>
      <h2>User activity report</h2>
      <div className="row" style={{ marginBottom: 10 }}>
        {(
          [
            ['7', 'Last 7 days'],
            ['30', 'Last 30 days'],
            ['all', 'All time'],
          ] as const
        ).map(([k, l]) => (
          <button key={k} className={`btn small ${period === k ? 'on' : ''}`} onClick={() => setPeriod(k)}>
            {l}
          </button>
        ))}
      </div>
      <div className="panel scroll" style={{ padding: '4px 8px' }}>
        <table>
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              {kinds.map((k) => (
                <th key={k}>{k}</th>
              ))}
              <th>Total</th>
              <th>Last activity</th>
            </tr>
          </thead>
          <tbody>
            {d.b.users.map((u) => {
              const last = rep.data?.last.find((l) => l.user === u.name);
              return (
                <tr key={u.id} className="click" onClick={() => onOpenUser(u.name)}>
                  <td>
                    <b>{u.name}</b>
                    {!u.active && (
                      <>
                        {' '}
                        <Tag cls="soft">removed</Tag>
                      </>
                    )}
                  </td>
                  <td className="meta">{roleOf(u.id, u.role)}</td>
                  {kinds.map((k) => (
                    <td key={k}>{count(u.name, k) || <span className="meta">0</span>}</td>
                  ))}
                  <td>
                    <b>{total(u.name)}</b>
                  </td>
                  <td className="meta">{last ? `${ymd(last.at)}, ${hm(last.at)}` : 'never'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <h2>Roles</h2>
      <div className="panel">
        <p style={{ margin: '0 0 6px' }}>
          <b>Warehouse owner:</b> can do everything. The only one with the key and app access to L1. Adds users, storage areas, project codes and article minimums, and approves reserved items for other projects.
        </p>
        <p style={{ margin: '0 0 6px' }}>
          <b>Checker:</b> not a fixed person. One of the users takes the role for a period and confirms suitcase checks.
        </p>
        <p style={{ margin: 0 }}>
          <b>User:</b> registers, takes out and moves items (not in L1), records QC, adds notes, sends the daily report.
        </p>
      </div>
      <h2>Checker on duty</h2>
      <div className="panel row">
        {d.isOwner ? (
          <>
            <label className="field">
              Who is Checker now?
              <select value={d.b.checkerId ?? ''} onChange={(e) => void run(() => post('/api/checker', { userId: Number(e.target.value) }), 'Checker changed')}>
                {!d.b.checkerId && <option value="">Nobody</option>}
                {d.b.users
                  .filter((u) => u.active)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
              </select>
            </label>
            <span className="meta" style={{ alignSelf: 'flex-end' }}>
              The role rotates between users. How often is not set yet.
            </span>
          </>
        ) : (
          <span>{d.b.users.find((u) => u.id === d.b.checkerId)?.name ?? 'Nobody'} is Checker on duty now.</span>
        )}
      </div>
      {d.isOwner && (
        <>
          <h2>
            Manage users ({active} of {MAX_USERS})
          </h2>
          <div className="panel">
            {d.b.users.map((u) => (
              <div key={u.id} className="row" style={{ padding: '6px 0', borderBottom: '1px solid var(--line)' }}>
                <b style={{ minWidth: 130 }}>{u.name}</b>
                {u.id === d.me.id ? (
                  <>
                    <span className="meta" style={{ minWidth: 140 }}>
                      {ROLE_LABEL[u.role]}
                    </span>
                    <span className="meta">you</span>
                  </>
                ) : (
                  <>
                    <select value={u.role} onChange={(e) => void run(() => patch(`/api/users/${u.id}`, { role: e.target.value }), 'Role changed')}>
                      <option value="owner">{ROLE_LABEL.owner}</option>
                      <option value="user">{ROLE_LABEL.user}</option>
                    </select>
                    {u.active ? (
                      <button className="btn small" onClick={() => void run(() => patch(`/api/users/${u.id}`, { active: false }), `${u.name} removed`)}>
                        Remove
                      </button>
                    ) : (
                      <button className="btn small" onClick={() => void run(() => patch(`/api/users/${u.id}`, { active: true }), `${u.name} added back`)}>
                        Add back
                      </button>
                    )}
                    {pinFor?.id === u.id ? (
                      <>
                        <input inputMode="numeric" maxLength={6} placeholder="new PIN" style={{ width: 100 }} value={pinFor.pin} onChange={(e) => setPinFor({ id: u.id, pin: e.target.value })} />
                        <button className="btn small primary" onClick={async () => (await run(() => patch(`/api/users/${u.id}`, { pin: pinFor.pin }), 'PIN changed')) && setPinFor(null)}>
                          Save PIN
                        </button>
                      </>
                    ) : (
                      <button className="btn small" onClick={() => setPinFor({ id: u.id, pin: '' })}>
                        Reset PIN
                      </button>
                    )}
                  </>
                )}
              </div>
            ))}
            <div className="row" style={{ marginTop: 14, alignItems: 'flex-end' }}>
              <label className="field">
                Name
                <input placeholder="Name" value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} />
              </label>
              <label className="field">
                Role
                <select value={nu.role} onChange={(e) => setNu({ ...nu, role: e.target.value as Role })}>
                  <option value="user">{ROLE_LABEL.user}</option>
                  <option value="owner">{ROLE_LABEL.owner}</option>
                </select>
              </label>
              <label className="field">
                PIN
                <input inputMode="numeric" maxLength={6} placeholder="4–6 digits" style={{ width: 100 }} value={nu.pin} onChange={(e) => setNu({ ...nu, pin: e.target.value })} />
              </label>
              <button
                className="btn primary"
                disabled={active >= MAX_USERS}
                onClick={async () => {
                  if (await run(() => post('/api/users', nu), `${nu.name} can now sign in`)) setNu({ name: '', role: 'user', pin: '' });
                }}
              >
                Add user
              </button>
            </div>
          </div>
        </>
      )}
      <h2>My PIN</h2>
      <div className="panel row" style={{ alignItems: 'flex-end' }}>
        <label className="field">
          Current PIN
          <input type="password" inputMode="numeric" maxLength={6} style={{ width: 110 }} value={mine.old} onChange={(e) => setMine({ ...mine, old: e.target.value })} />
        </label>
        <label className="field">
          New PIN
          <input type="password" inputMode="numeric" maxLength={6} style={{ width: 110 }} value={mine.pin} onChange={(e) => setMine({ ...mine, pin: e.target.value })} />
        </label>
        <button className="btn" onClick={async () => (await run(() => post('/api/me/pin', mine), 'Your PIN is changed')) && setMine({ old: '', pin: '' })}>
          Change my PIN
        </button>
      </div>
      <Err e={err} />
    </>
  );
}
